/**
 * Counts a `/redirect` visit in Plausible without handing Plausible the link.
 *
 * `/redirect/<token>` URLs (and their `link` / `key` query) are single-use
 * shopper links. PR #5442 stopped every third-party script loading on the page
 * while we hunted a leak; the leak turned out to be Zaraz's TikTok tool, and
 * Plausible was still loaded during the window when replays fell to zero, so
 * it was not the source. We still never send it the real address: this posts
 * one pageview to Plausible's events API with the path fixed to `/redirect`
 * and the referrer cut to its origin, while the Plausible script itself stays
 * off the page (and `data-exclude` in AppProviders stops the auto pageview a
 * soft navigation would otherwise send).
 *
 * Mirrors the script's own skips: localhost, automated browsers, and a
 * `plausible_ignore` flag (set for the spoofed in-app bot fleet by
 * `spoofedInAppBrowser.ts`, or by a teammate excluding themselves).
 */
const PLAUSIBLE_EVENT_ENDPOINT = 'https://plausible.io/api/event'
/** The Plausible site id. AppProviders' PlausibleProvider uses it too. */
export const PLAUSIBLE_DOMAIN = 'dupe.com'

const originOf = (url: string): string | null => {
  try {
    return new URL(url).origin
  } catch {
    return null
  }
}

export function sendRedirectPageviewToPlausible(
  location?: Pick<Location, 'hostname' | 'origin'>
): void {
  if (typeof window === 'undefined') return
  const { hostname, origin } = location ?? window.location
  if (/^localhost$|^127(\.[0-9]+){0,2}\.[0-9]+$|^\[::1?\]$/.test(hostname))
    return
  if (window.navigator.webdriver) return
  try {
    if (window.localStorage.getItem('plausible_ignore') === 'true') return
  } catch {
    // Storage blocked: count the visit, as the script would.
  }

  const body = JSON.stringify({
    n: 'pageview',
    u: `${origin}/redirect`,
    d: PLAUSIBLE_DOMAIN,
    r: document.referrer ? originOf(document.referrer) : null,
  })
  fetch(PLAUSIBLE_EVENT_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain' },
    body,
    keepalive: true,
    // The Referer header would otherwise carry this page's URL; `r` above
    // already holds the only part we mean to send.
    referrerPolicy: 'no-referrer',
  }).catch(() => {})
}
